/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import type { BannerActualidad as BannerActualidadType } from '@/interfaces/strapi';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';

type BannerActualidadProps = {
	readonly className?: string;
	readonly items: BannerActualidadType[];
	readonly intervalMs?: number;
	readonly heightPx?: number | string;
	readonly hasDots?: boolean;
	readonly hasArrows?: boolean;
};

const classNameComponent = 'c-cnm-banner-actualidad';

export const BannerActualidad = ({
	className,
	items,
	intervalMs = 5000,
	heightPx = 'auto',
	hasDots = true,
	hasArrows = true,
}: BannerActualidadProps) => {
	const wrapperClassName = useMemo(() => classNames(classNameComponent, className), [className]);

	const [viewportRef, embla] = useEmblaCarousel({
		loop: true,
		align: 'start',
		containScroll: 'trimSnaps',
	});

	const [paused, setPaused] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	const onSelect = useCallback(() => {
		if (!embla) return;
		setSelectedIndex(embla.selectedScrollSnap());
	}, [embla]);

	useEffect(() => {
		if (!embla) return;
		setSnapCount(embla.scrollSnapList().length);
		onSelect();
		embla.on('select', onSelect);
		embla.on('reInit', onSelect);
		return () => {
			embla?.off?.('select', onSelect);
			embla?.off?.('reInit', onSelect);
		};
	}, [embla, onSelect]);

	// Autoplay simple
	useEffect(() => {
		if (!embla || paused || items.length <= 1) return;
		const id = setInterval(() => embla.scrollNext(), intervalMs);
		return () => clearInterval(id);
	}, [embla, paused, intervalMs, items.length]);

	if (!items?.length) return null;

	return (
		<section
			className={wrapperClassName}
			role="region"
			aria-roledescription="carousel"
			aria-label="Carrusel de actualidad"
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
		>
			<div className="o-container">
				{hasArrows && items.length > 1 && (
					<>
						<button
							type="button"
							aria-label="Anterior"
							className={`${classNameComponent}__arrow ${classNameComponent}__arrow--prev`}
							onClick={() => embla?.scrollPrev()}
						>
							<CTNIcon icon="arrowLeft" />
						</button>
						<button
							type="button"
							aria-label="Siguiente"
							className={`${classNameComponent}__arrow ${classNameComponent}__arrow--next`}
							onClick={() => embla?.scrollNext()}
						>
							<CTNIcon icon="arrowRight" />
						</button>
					</>
				)}

				<div ref={viewportRef} className={`${classNameComponent}__viewport`}>
					<div className={`${classNameComponent}__container`}>
						{items.map((item, idx) => {
							const body = (
								<div className={`${classNameComponent}__item-inner`}>
									<h3 className={`${classNameComponent}__title`} title={item.title}>
										{item.title}
									</h3>
									{item.description && (
										<p className={`${classNameComponent}__desc`}>{item.description}</p>
									)}
								</div>
							);

							return (
								<div
									key={idx}
									className={`${classNameComponent}__slide`}
									role="group"
									aria-roledescription="slide"
									aria-label={`Elemento ${idx + 1} de ${items.length}`}
								>
									{item.url ? (
										<a
											href={item.url}
											className={`${classNameComponent}__link`}
											aria-label={item.title}
										>
											{body}
										</a>
									) : (
										body
									)}
								</div>
							);
						})}
					</div>
				</div>

				{hasDots && snapCount > 1 && (
					<div className={`${classNameComponent}__dots`} role="tablist" aria-label="Paginación del carrusel">
						{Array.from({ length: snapCount }).map((_, i) => (
							<button
								key={i}
								type="button"
								className={classNames(`${classNameComponent}__dot`, i === selectedIndex && 'is-active')}
								role="tab"
								aria-selected={i === selectedIndex}
								aria-label={`Ir al elemento ${i + 1}`}
								onClick={() => embla?.scrollTo(i)}
							/>
						))}
					</div>
				)}
			</div>
		</section>
	);
};

export default BannerActualidad;
